/**
 * DocumentCard Component - Enhanced document card with status
 */

import React, { useState } from 'react'
import { Card, Badge } from '../ui'
import { 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Upload, 
  Eye, 
  Download, 
  XCircle,
  FileEdit,
  Archive,
  ExternalLink,
  RefreshCw
} from 'lucide-react'

// ✅ Sanitization helper - prevent XSS
function sanitizeText(text) {
  if (!text) return ''
  // Remove HTML tags and dangerous characters
  return text
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .trim()
}

const STATUS_META = {
  uploaded: { label: 'Uploaded', icon: Upload, variant: 'primary' },
  pending: { label: 'Processing', icon: Clock, variant: 'warning' },
  missing: { label: 'Missing', icon: AlertTriangle, variant: 'error' },
  draft: { label: 'Draft', icon: FileEdit, variant: 'primary' },
  awaiting: { label: 'Awaiting Verification', icon: Clock, variant: 'warning' },
  verified: { label: 'Verified', icon: CheckCircle, variant: 'success' },
  rejected: { label: 'Rejected', icon: XCircle, variant: 'error' },
  archived: { label: 'Archived', icon: Archive, variant: 'default' },
}

const DocumentCard = ({
  document,
  onUpload,
  onView,
  onDownload,
  onRequestAgent,
  onApprove,
  onReject,
  onReupload,
  signedUrl = null,
  className = '',
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const meta = STATUS_META[document.status] || STATUS_META.missing
  const StatusIcon = meta.icon

  const isCritical = document.critical && document.status === 'missing'
  const isVerified = document.status === 'verified'
  const isUploaded = document.status === 'uploaded' || document.status === 'awaiting'
  const isRejected = document.status === 'rejected'

  // ✅ Sanitize all text for display
  const safeLabel = sanitizeText(document.label)
  const safeFileName = sanitizeText(document.fileName || '')
  const safeRejectReason = sanitizeText(document.rejectReason || '')

  return (
    <Card
      className={`
        p-5 transition-all duration-200
        ${isCritical ? 'border-error border-2' : ''}
        ${isHovered ? 'shadow-md -translate-y-0.5' : ''}
        ${className}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`
            w-9 h-9 rounded-lg flex items-center justify-center shrink-0
            ${isVerified ? 'bg-success-soft' : isCritical ? 'bg-error-soft' : 'bg-neutral-100'}
          `}>
            <FileText size={16} className={isVerified ? 'text-success' : isCritical ? 'text-error' : 'text-neutral-400'} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink truncate flex items-center gap-1.5">
              {safeLabel}
              {isCritical && (
                <Badge variant="error" size="sm">Critical</Badge>
              )}
            </p>
            {document.fileName && (
              <p className="text-xs text-muted truncate font-mono">{safeFileName}</p>
            )}
          </div>
        </div>
        <Badge variant={meta.variant} size="sm" icon={<StatusIcon size={10} />}>
          {meta.label}
        </Badge>
      </div>

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-neutral-100 flex flex-wrap gap-2">
        {document.status === 'missing' && (
          <>
            <button
              onClick={() => onUpload?.(document.id)}
              className="btn-ghost text-xs flex items-center gap-1.5"
            >
              <Upload size={13} /> Upload
            </button>
            <button
              onClick={() => onRequestAgent?.(document)}
              className="btn-primary text-xs flex items-center gap-1.5"
            >
              <AlertTriangle size={13} /> Request Agent
            </button>
          </>
        )}

        {isUploaded && (
          <>
            {signedUrl && (
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost text-xs flex items-center gap-1.5"
              >
                <Eye size={13} /> View
              </a>
            )}
            <button
              onClick={() => onReupload?.(document.id)}
              className="btn-secondary text-xs flex items-center gap-1.5"
            >
              <RefreshCw size={13} /> Re-upload
            </button>
          </>
        )}

        {document.status === 'pending' && (
          <span className="text-xs text-muted flex items-center gap-1.5 py-1">
            <Clock size={13} className="text-warning" /> Processing...
          </span>
        )}

        {document.status === 'draft' && (
          <>
            <button
              onClick={() => onView?.(document)}
              className="btn-secondary text-xs flex items-center gap-1.5"
            >
              <Eye size={13} /> Review Draft
            </button>
            <button
              onClick={() => onApprove?.(document.id)}
              className="btn-primary text-xs flex items-center gap-1.5"
            >
              <CheckCircle size={13} /> Approve
            </button>
          </>
        )}

        {document.status === 'awaiting' && (
          <div className="w-full">
            <span className="inline-flex items-center gap-1 text-xs text-muted">
              <Clock size={13} className="text-warning" /> Awaiting platform verification
            </span>
            <p className="text-[11px] text-muted mt-1">Expected: 2-3 business days</p>
          </div>
        )}

        {isVerified && (
          <span className="inline-flex items-center gap-1 text-xs text-success font-semibold">
            <CheckCircle size={13} /> Verified — visible to investors
          </span>
        )}

        {isRejected && (
          <div className="w-full space-y-2">
            {document.rejectReason && (
              <p className="text-xs text-error">Reason: {safeRejectReason}</p>
            )}
            <button
              onClick={() => onReupload?.(document.id)}
              className="btn-ghost text-xs flex items-center gap-1.5"
            >
              <RefreshCw size={13} /> Re-upload
            </button>
          </div>
        )}
      </div>
    </Card>
  )
}

DocumentCard.displayName = 'DocumentCard'

export default DocumentCard